import { useState, useEffect, useCallback } from 'react';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import { HelpPanel } from '../../components/admin/HelpPanel';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  getAgentRuns,
  getAgentRun,
  getAgentRunAggregate,
  getAgentMode,
  setAgentMode,
  clearAgentRuns,
  type AgentRunRow,
  type AgentRunDetail,
  type AgentAggregates,
} from '../../lib/admin-api';
import { Loader2, AlertCircle, RefreshCw, Trash2 } from 'lucide-react';

const PAGE_SIZE = 25;

function fmtNum(v: number | null): string {
  return v === null || v === undefined ? '—' : v.toLocaleString();
}

export default function AgentRunsView() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();

  const [rows, setRows] = useState<AgentRunRow[]>([]);
  const [total, setTotal] = useState(0);
  const [aggregate, setAggregate] = useState<AgentAggregates | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modeFilter, setModeFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState(1);

  const [globalMode, setGlobalMode] = useState<'legacy' | 'agentic' | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const [modeLoading, setModeLoading] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState<'legacy' | 'agentic' | null>(null);

  const [selectedRun, setSelectedRun] = useState<AgentRunDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    if (modeFilter !== 'all') params.append('mode', modeFilter);
    if (dateFrom) params.append('from', dateFrom);
    if (dateTo) params.append('to', dateTo);
    params.append('page', String(page));
    params.append('pageSize', String(PAGE_SIZE));
    return params;
  }, [modeFilter, dateFrom, dateTo, page]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const [runsResult, aggResult] = await Promise.all([
        getAgentRuns(params),
        getAgentRunAggregate(params),
      ]);
      setRows(runsResult.rows);
      setTotal(runsResult.total);
      setAggregate(aggResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent runs');
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  useEffect(() => {
    if (!isAuthenticated) return;
    setModeLoading(true);
    setModeError(null);
    getAgentMode()
      .then((res) => { setGlobalMode(res.mode); setModelId(res.modelId); })
      .catch((err) => setModeError(err instanceof Error ? err.message : 'Failed to load agent mode'))
      .finally(() => setModeLoading(false));
  }, [isAuthenticated]);

  const requestModeChange = (checked: boolean) => {
    setPendingMode(checked ? 'agentic' : 'legacy');
    setConfirmOpen(true);
  };

  const confirmModeChange = async () => {
    if (!pendingMode) return;
    setModeLoading(true);
    setModeError(null);
    try {
      await setAgentMode(pendingMode);
      setGlobalMode(pendingMode);
    } catch (err) {
      setModeError(err instanceof Error ? err.message : 'Failed to update agent mode');
    } finally {
      setModeLoading(false);
      setConfirmOpen(false);
      setPendingMode(null);
    }
  };

  const openRow = async (id: string) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerError(null);
    setSelectedRun(null);
    try {
      const detail = await getAgentRun(id);
      setSelectedRun(detail);
    } catch (err) {
      setDrawerError(err instanceof Error ? err.message : 'Failed to load run detail');
    } finally {
      setDrawerLoading(false);
    }
  };

  const confirmClearRuns = async () => {
    setIsClearing(true);
    setError(null);
    try {
      await clearAgentRuns();
      setPage(1);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear runs');
    } finally {
      setIsClearing(false);
      setClearConfirmOpen(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <AdminLayout title="Agent Runs">
      <HelpPanel storageKey="agent-runs">
        <p>
          This page controls and monitors which chat pipeline <strong>real patient sessions</strong> use.
          <strong> Legacy</strong> sends a fixed chart summary — demographics, conditions, medications,
          allergies, vitals and labs, plus metadata (not text) for the 10 most recent notes — assembled in
          priority order under a ~8k-token budget (lower-priority sections are dropped when they don&apos;t
          fit), with a few question-relevant note excerpts added. <strong>Agentic</strong> lets the AI
          retrieve exactly the records each question needs — including full note text — from anywhere in
          the chart.
        </p>
        <p>
          The switch below takes effect immediately — even mid-conversation — and agentic mode requires the AWS Bedrock
          provider — otherwise sessions silently use legacy.
        </p>
        <p>
          Each row in the Runs table is one AI answer, compared against <strong>Context Stuffing</strong> —
          what sending the <em>entire uncapped chart</em> (all sections, every clinical note in full, plus the
          conversation so far) in one prompt would take. The legacy path itself truncates to ~8k tokens, so it
          can silently drop records agentic can still reach. Context Stuffing figures are chars/4 estimates;
          token counts on runs are measured usage.
        </p>
      </HelpPanel>

      {/* Global mode toggle */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Switch
              id="global-agent-mode"
              checked={globalMode === 'agentic'}
              disabled={modeLoading || globalMode === null}
              onCheckedChange={requestModeChange}
              aria-label="Agentic mode (global default)"
            />
            <Label htmlFor="global-agent-mode" className="cursor-pointer">
              Agentic mode (global default)
            </Label>
            {modeLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
            <span className="text-xs text-gray-400">
              Current: {globalMode ? globalMode : '—'}
            </span>
            {modelId && (
              <span className="text-xs text-gray-400">
                Model: <span className="font-mono">{modelId}</span>
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2 ml-12">
            Takes effect on the next message of every session.
          </p>
        </CardContent>
      </Card>

      {modeError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{modeError}</AlertDescription>
        </Alert>
      )}

      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all runs?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every recorded run trace ({total} total). Chat conversations are not
              affected — only the Agent Runs metrics history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={confirmClearRuns}>
              Clear all runs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change global agent mode?</AlertDialogTitle>
            <AlertDialogDescription>
              This changes the default for ALL new sessions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingMode(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmModeChange}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <Label htmlFor="modeFilter" className="text-sm text-gray-600">Mode</Label>
          <Select value={modeFilter} onValueChange={(v) => { setModeFilter(v); setPage(1); }}>
            <SelectTrigger id="modeFilter" className="w-40">
              <SelectValue placeholder="All modes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              <SelectItem value="agentic">Agentic</SelectItem>
              <SelectItem value="legacy">Legacy</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="dateFrom" className="text-sm text-gray-600">From</Label>
          <Input
            id="dateFrom"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="w-40"
          />
        </div>
        <div>
          <Label htmlFor="dateTo" className="text-sm text-gray-600">To</Label>
          <Input
            id="dateTo"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="w-40"
          />
        </div>
        <Button onClick={fetchData} disabled={isLoading} size="sm">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && rows.length === 0 && !aggregate ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          {aggregate && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tool Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  {aggregate.toolUsage.length === 0 ? (
                    <p className="text-gray-500 text-sm">No tool calls yet</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                      {aggregate.toolUsage.map((t) => (
                        <Card key={t.tool}>
                          <CardContent className="pt-4 pb-4">
                            <p className="text-xs text-gray-500 font-mono truncate" title={t.tool}>{t.tool}</p>
                            <p className="text-2xl font-bold tabular-nums">{t.uses}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Runs table */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg">Runs</CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
                disabled={isClearing || rows.length === 0}
                onClick={() => setClearConfirmOpen(true)}
              >
                {isClearing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Clear runs
              </Button>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <p className="text-gray-500 text-sm">No runs found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 pr-4">Created</th>
                        <th className="pb-2 pr-4">Setting</th>
                        <th className="pb-2 pr-4">Mode</th>
                        <th className="pb-2 pr-4">Model</th>
                        <th className="pb-2 pr-4 text-right">Turns</th>
                        <th className="pb-2 pr-4 text-right">Tool Calls</th>
                        <th className="pb-2 pr-4 text-right">In Tokens</th>
                        <th className="pb-2 pr-4 text-right">Out Tokens</th>
                        <th className="pb-2 pr-4 text-right">Total Tokens</th>
                        <th className="pb-2 pr-4 text-right">Context Stuffing</th>
                        <th className="pb-2 pr-4 text-right">Latency</th>
                        <th className="pb-2 text-right">Cost (est.)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((run) => (
                        <tr
                          key={run.id}
                          data-testid="agent-run-row"
                          className="border-b last:border-0 cursor-pointer hover:bg-gray-50"
                          onClick={() => openRow(run.id)}
                        >
                          <td className="py-2 pr-4 text-xs text-gray-600">{new Date(run.createdAt).toLocaleString()}</td>
                          <td className="py-2 pr-4 text-xs">{run.settingId ?? '—'}</td>
                          <td className="py-2 pr-4">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                run.mode === 'agentic' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {run.mode}
                            </span>
                            {run.fellBack && (
                              <span className="ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-orange-50 text-orange-700">
                                fallback
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-4 font-mono text-xs text-gray-600 max-w-[16ch] truncate" title={run.modelId}>{run.modelId}</td>
                          <td className="py-2 pr-4 text-right">{run.turns.length}</td>
                          <td className="py-2 pr-4 text-right">{run.toolCalls.length}</td>
                          <td className="py-2 pr-4 text-right">{fmtNum(run.inputTokens)}</td>
                          <td className="py-2 pr-4 text-right">{fmtNum(run.outputTokens)}</td>
                          <td className="py-2 pr-4 text-right font-medium">{fmtNum(run.inputTokens + run.outputTokens)}</td>
                          <td className="py-2 pr-4 text-right">{fmtNum(run.baselineTokensEst)}</td>
                          <td className="py-2 pr-4 text-right">{run.responseTimeMs}ms</td>
                          <td className="py-2 text-right">${run.costUsdEst.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                <span>
                  Page {page} of {totalPages} ({total} total)
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1 || isLoading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || isLoading}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detail drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Run Detail</SheetTitle>
            <SheetDescription>
              {selectedRun ? `${selectedRun.mode} · ${selectedRun.modelId}` : 'Loading run...'}
            </SheetDescription>
          </SheetHeader>

          {drawerLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          )}

          {drawerError && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{drawerError}</AlertDescription>
            </Alert>
          )}

          {selectedRun && !drawerLoading && (
            <div className="mt-4 space-y-6">
              {/* Token meter */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Token Usage</h3>
                <TokenMeter
                  label="Context Stuffing"
                  value={selectedRun.baselineTokensEst}
                  max={Math.max(selectedRun.baselineTokensEst ?? 0, selectedRun.inputTokens + selectedRun.outputTokens, 1)}
                  colorClass="bg-gray-400"
                  estimated
                />
                <TokenMeter
                  label="Actual"
                  value={selectedRun.inputTokens + selectedRun.outputTokens}
                  max={Math.max(selectedRun.baselineTokensEst ?? 0, selectedRun.inputTokens + selectedRun.outputTokens, 1)}
                  colorClass="bg-blue-500"
                />
              </div>

              {/* Turns table */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Turns</h3>
                {selectedRun.turns.length === 0 ? (
                  <p className="text-gray-500 text-sm">No turns recorded</p>
                ) : (
                  <table className="w-full text-xs" data-testid="turns-table">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-1 pr-2">Turn</th>
                        <th className="pb-1 pr-2 text-right">In</th>
                        <th className="pb-1 pr-2 text-right">Out</th>
                        <th className="pb-1 pr-2 text-right">Model ms</th>
                        <th className="pb-1 pr-2 text-right">Tools ms</th>
                        <th className="pb-1">Stop Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRun.turns.map((t) => (
                        <tr key={t.turn} className="border-b last:border-0">
                          <td className="py-1 pr-2">{t.turn}</td>
                          <td className="py-1 pr-2 text-right">{t.inputTokens}</td>
                          <td className="py-1 pr-2 text-right">{t.outputTokens}</td>
                          <td className="py-1 pr-2 text-right">{t.modelMs}</td>
                          <td className="py-1 pr-2 text-right">{t.toolsMs}</td>
                          <td className="py-1">{t.stopReason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Tool calls table */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Tool Calls</h3>
                {selectedRun.toolCalls.length === 0 ? (
                  <p className="text-gray-500 text-sm">No tool calls recorded</p>
                ) : (
                  <table className="w-full text-xs" data-testid="tool-calls-table">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-1 pr-2">Tool</th>
                        <th className="pb-1 pr-2">Args</th>
                        <th className="pb-1 pr-2 text-right">Result (est.)</th>
                        <th className="pb-1 pr-2 text-right">ms</th>
                        <th className="pb-1">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRun.toolCalls.map((tc, i) => (
                        <tr key={i} className="border-b last:border-0 align-top">
                          <td className="py-1 pr-2 font-mono">{tc.tool}</td>
                          <td className="py-1 pr-2 font-mono text-gray-500 max-w-xs break-all">
                            {JSON.stringify(tc.args)}
                          </td>
                          <td className="py-1 pr-2 text-right">{tc.resultTokensEst}</td>
                          <td className="py-1 pr-2 text-right">{tc.durationMs}</td>
                          <td className="py-1">
                            {tc.error ? (
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700">
                                error
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}


function TokenMeter({
  label,
  value,
  max,
  colorClass,
  estimated = false,
}: {
  label: string;
  value: number | null;
  max: number;
  colorClass: string;
  estimated?: boolean;
}) {
  const suffix = estimated ? ' (est.)' : '';
  const displayValue = value === null ? `—${suffix}` : `${value.toLocaleString()}${suffix}`;
  const width = value === null ? 0 : Math.max((value / max) * 100, value > 0 ? 2 : 0);
  return (
    <div className="mb-2">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{label}</span>
        <span>{displayValue}</span>
      </div>
      <div className="bg-gray-100 rounded-full h-3 overflow-hidden">
        <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}
